import React, { useContext, useEffect, useState } from "react";
import {
	DefaultErrorMessage,
	FormKeys,
	ZomentumEntities
} from "@/Contracts";
import {
	AddLogCallRequest,
	ESalesActivityType,
	LogCallFormValues,
	SalesActivity
} from "@/Contracts/Others/SalesActivity";
import SalesActivityActionCreators from "../../../../store/actions/SalesActivity";
import ZMessage from "@molecules/ZMessage";
import ZLogCallDrawer from "../../../../templates/ZDrawers/ZSalesActivities/ZLogCall";
import { IAddSalesActivityDrawerProps } from "../interface";
import {
	SalesActivityModuleState,
	useSalesActivityModuleDispatch,
	useSalesActivityModuleSelector
} from "../../../../store";
import Form from "antd/lib/form";
import moment from "moment";
import SalesActivityAddWrapper from "../Wrappers/AddWrapper";
import SalesActivityEditWrapper from "../Wrappers/EditWrapper";
import {
	ILogCallUIProps,
	IOnlyLogCallUIProps
} from "../../../../templates/ZDrawers/ZSalesActivities/interface";
import useDatePattern from "@/Hooks/useDatePattern";
import { ParentContext } from "../../../../store/context/ParentContext";
import ZModalConfirm from "@atoms/ZModalConfirmation";
import { isPartnerApp } from "@/Utils";

const LogCall: React.FC<IAddSalesActivityDrawerProps> = ({
	visible = false,
	isEditMode = false,
	onClose,
	onSubmit
}) => {
	const [isCrossAssign, setIsCrossAssign] = useState(false);
	const parentContext = useContext(ParentContext);
	const { currentActiveSalesActivity } = useSalesActivityModuleSelector(
		(state: SalesActivityModuleState) => {
			return {
				currentActiveSalesActivity:
					state.salesActivity.currentActiveSalesActivity
			};
		}
	);
	const { userData } = parentContext.user;
	const { isFormEdited } = parentContext.setting;

	const [form] = Form.useForm();
	const dispatch = useSalesActivityModuleDispatch();

	const datePattern = useDatePattern();

	const getCallLogCustomFields = async () => {
		try {
			await parentContext.setting.getCustomFields(
				ZomentumEntities.SalesLogCall
			);
		} catch (err) {
			console.error(err);
		}
	};

	useEffect(() => {
		if (visible) {
			if (isEditMode && !!currentActiveSalesActivity?.feed_details) {
				const feed = currentActiveSalesActivity.feed_details;
				form.setFieldsValue({
					title: feed.title,
					description: feed.description,
					date: moment(feed.date_time),
					time: moment(feed.date_time),
					task_type: feed.task_type,
					priority: feed.priority,
					collaborator_ids: feed.collaborator_ids,
					relation: feed.entity_type,
					outcome: feed.outcome,
					author_id: feed.author_id
				});
				setIsCrossAssign(checkIfCrossAssign(feed.author_id));
			}
			getCallLogCustomFields();
		} else {
			setIsCrossAssign(false);
		}
	}, [visible, currentActiveSalesActivity]);

	const handleClose = () => {
		form.resetFields();
		onClose && onClose();
		parentContext.setting.resetFormEdited?.(FormKeys.AddEditCallLog);
	};

	const closeLogCallDrawer = () => {
		if (!isFormEdited?.[FormKeys.AddEditCallLog]) {
			handleClose();
		} else {
			ZModalConfirm({
				content: `Are you sure you want to discard ${
					isEditMode ? "editing" : "adding"
				} call log?`,
				onOk: handleClose
			});
		}
	};

	const getFormValues = (addLogCallFormValues: LogCallFormValues) => {
		const addLogCallRequest = new AddLogCallRequest();
		addLogCallRequest.title = addLogCallFormValues.title;
		addLogCallRequest.description = addLogCallFormValues.description;
		addLogCallRequest.outcome = addLogCallFormValues.outcome;
		const dateTime = new Date();
		dateTime.setDate(1);
		dateTime.setFullYear(addLogCallFormValues.date.year());
		dateTime.setMonth(addLogCallFormValues.date.month());
		dateTime.setDate(addLogCallFormValues.date.date());
		dateTime.setHours(addLogCallFormValues.time.hours());
		dateTime.setMinutes(addLogCallFormValues.time.minutes());
		dateTime.setSeconds(0);
		dateTime.setMilliseconds(0);
		addLogCallRequest.date_time = dateTime;
		addLogCallRequest.author_id =
			addLogCallFormValues.author_id ?? userData?.id;
		addLogCallRequest.entity_type = addLogCallFormValues.relation;
		if (addLogCallFormValues.relation === ZomentumEntities.Opportunity) {
			addLogCallRequest.entity_id = addLogCallFormValues.opportunity;
		} else if (addLogCallFormValues.relation === ZomentumEntities.Client) {
			addLogCallRequest.entity_id = addLogCallFormValues.client_company;
		} else if (
			addLogCallFormValues.relation === ZomentumEntities.VendorCompany
		) {
			addLogCallRequest.entity_id = addLogCallFormValues.company;
		} else if (
			addLogCallFormValues.relation === ZomentumEntities.VendorOpportunity
		) {
			addLogCallRequest.entity_id =
				addLogCallFormValues.vendor_opportunity;
		} else if (
			addLogCallFormValues.relation === ZomentumEntities.VendorClient
		) {
			addLogCallRequest.entity_id =
				addLogCallFormValues.vendor_client_company;
		}
		return addLogCallRequest;
	};

	const addLogCall = async (addLogCallFormValues: LogCallFormValues) => {
		dispatch(SalesActivityActionCreators.showSalesActivityLoader());
		try {
			const addLogCallRequest = getFormValues(addLogCallFormValues);
			const addLogCallResponse = await dispatch(
				SalesActivityActionCreators.addLogCall(
					addLogCallRequest,
					isCrossAssign
				)
			);
			if (onSubmit) {
				onSubmit(addLogCallResponse.addLogCall);
			}
			handleClose();
			ZMessage.success("Call log added successfully");
		} catch (error) {
			console.error(error);
		} finally {
			dispatch(SalesActivityActionCreators.hideSalesActivityLoader());
		}
	};

	const editLogCall = async (editLogCallFormValues: LogCallFormValues) => {
		dispatch(SalesActivityActionCreators.showSalesActivityLoader());
		try {
			if (!!currentActiveSalesActivity?.feed_details) {
				const editLogCallRequest: SalesActivity = {
					...currentActiveSalesActivity
				};
				const formValues = getFormValues(editLogCallFormValues);
				editLogCallRequest.feed_details = {
					...currentActiveSalesActivity.feed_details,
					...formValues
				};
				const editLogCallResponse = await dispatch(
					SalesActivityActionCreators.editLogCall(editLogCallRequest)
				);
				if (onSubmit) {
					onSubmit(
						editLogCallResponse.editLogCall,
						form.getFieldValue(ZomentumEntities.Opportunity)
					);
				}
				handleClose();
				ZMessage.success("Call log edited successfully");
			} else {
				ZMessage.error(DefaultErrorMessage);
			}
		} catch (error) {
			console.error(error);
		} finally {
			dispatch(SalesActivityActionCreators.hideSalesActivityLoader());
		}
	};

	const handleFormChange = () => {
		!isFormEdited?.[FormKeys.AddEditCallLog] &&
			parentContext.setting.setFormEdited?.(FormKeys.AddEditCallLog);
	};

	const handleOwnerChange = (value: string) => {
		setIsCrossAssign(checkIfCrossAssign(value));
	};

	const checkIfCrossAssign = (selectedUserId: string) => {
		const user = parentContext.user.companyUserList.find(
			user => user.id === selectedUserId
		);
		if (!user) {
			return true;
		} else {
			return false;
		}
	};

	const isEditable = !!currentActiveSalesActivity?.feed_details
		?.sharing_details
		? currentActiveSalesActivity.feed_details.sharing_details.is_editable
		: true;

	const addLogCallUIProps: IOnlyLogCallUIProps = {
		onSubmit: isEditMode ? editLogCall : addLogCall,
		dateFormat: datePattern,
		handleFormChange,
		handleOwnerChange,
		isReadOnly: !isEditable,
		isEditMode
	};

	return isEditMode ? (
		<SalesActivityEditWrapper<ILogCallUIProps, IOnlyLogCallUIProps>
			visible={visible}
			onClose={closeLogCallDrawer}
			Component={ZLogCallDrawer}
			form={form}
			formUIProps={addLogCallUIProps}
			salesActivityType={ESalesActivityType.LOGCALL}
		/>
	) : (
		<SalesActivityAddWrapper<ILogCallUIProps, IOnlyLogCallUIProps>
			visible={visible}
			onClose={closeLogCallDrawer}
			Component={ZLogCallDrawer}
			form={form}
			formUIProps={addLogCallUIProps}
			salesActivityType={ESalesActivityType.LOGCALL}
		/>
	);
};

export default LogCall;
